import { Add, CheckCircle, Delete, Error as ErrorIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useState } from "react";
import { hostname, isSafeUrl } from "../../../utils";

interface MCPIntegrationFormProps {
  onChange: (data: any) => void;
  onValidationChange: (isValid: boolean) => void;
  loading: boolean;
  editMode?: boolean;
  existingData?: any;
  initialData?: any;
  getToken: () => Promise<string>;
  companyId?: string;
  instanceName?: string;
}

const MCPIntegrationForm: React.FC<MCPIntegrationFormProps> = ({
  onChange,
  onValidationChange,
  loading,
  editMode = false,
  existingData,
  initialData,
  getToken,
  companyId,
  instanceName = "",
}) => {
  // Use instanceName from parent (the Instance Name field in the dialog)
  // Fall back to existingData for edit mode
  const serverName = instanceName || existingData?.instanceName || "";
  const [serverType] = useState(initialData?.serverType || "http"); // Only HTTP is supported in SaaS
  const [serverUrl, setServerUrl] = useState(
    initialData?.serverUrl || existingData?.parameters?.serverUrl || ""
  );
  const [description, setDescription] = useState(
    initialData?.description || existingData?.parameters?.description || ""
  );
  const [apiKey, setApiKey] = useState(
    initialData?.apiKey || existingData?.parameters?.apiKey || ""
  );
  const [useOAuth, setUseOAuth] = useState(
    initialData?.useOAuth ?? existingData?.parameters?.useOAuth ?? false
  );
  const [headers, setHeaders] = useState<{ key: string; value: string }[]>(
    initialData?.headers
      ? Object.entries(initialData.headers).map(([key, value]) => ({
          key,
          value: value as string,
        }))
      : existingData?.parameters?.headers
      ? Object.entries(existingData.parameters.headers).map(([key, value]) => ({
          key,
          value: value as string,
        }))
      : []
  );

  // Test connection state
  const [testing, setTesting] = useState(false);
  const [processingManualCallback, setProcessingManualCallback] =
    useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    tools?: any[];
  } | null>(null);

  // OAuth flow state
  const [oauthState, setOauthState] = useState<string | null>(null);
  const [manualCallbackUrl, setManualCallbackUrl] = useState("");
  const [oauthTokens, setOauthTokens] = useState<any>(
    initialData?.oauth_tokens || existingData?.parameters?.oauth_tokens || null
  );
  const [oauthClientInfo, setOauthClientInfo] = useState<any>(
    initialData?.oauth_client_info || existingData?.parameters?.oauth_client_info || null
  );

  // Prepare form data
  const getFormData = React.useCallback(() => {
    const data: any = {
      serverName,
      serverType,
      serverUrl,
      description,
      useOAuth,
    };

    if (apiKey) {
      data.apiKey = apiKey;
    }
    if (headers.length > 0) {
      data.headers = headers.reduce((acc, { key, value }) => {
        if (key && value) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, string>);
    }

    // Include OAuth tokens if available
    if (oauthTokens) {
      console.log("Using OAuth tokens:", oauthTokens);
      data.oauth_tokens = oauthTokens;
    }

    // Include OAuth client info if available
    if (oauthClientInfo) {
      console.log("Using OAuth client info:", oauthClientInfo);
      data.oauth_client_info = oauthClientInfo;
    }

    console.log("Form data prepared:", data);

    return data;
  }, [serverName, serverType, serverUrl, description, apiKey, headers, oauthTokens, oauthClientInfo, useOAuth, instanceName, existingData]);

  // Validate form and notify parent
  React.useEffect(() => {
    const isValid = !!(serverName && serverUrl);
    onValidationChange(isValid);
  }, [serverName, serverUrl, onValidationChange, instanceName, existingData]);

  // Update parent when data changes
  React.useEffect(() => {
    const data = getFormData();
    onChange(data);
  }, [getFormData, onChange]);

  const addHeader = () => {
    setHeaders([...headers, { key: "", value: "" }]);
  };

  const updateHeader = (
    index: number,
    field: "key" | "value",
    value: string
  ) => {
    const newHeaders = [...headers];
    newHeaders[index][field] = value;
    setHeaders(newHeaders);
  };

  const removeHeader = (index: number) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const handleManualCallback = async () => {
    if (!manualCallbackUrl.trim()) {
      setTestResult({
        success: false,
        message: "Please enter the callback URL",
      });
      return;
    }

    try {
      setProcessingManualCallback(true);
      const token = await getToken();

      // Submit the manual callback URL
      const response = await axios.post(
        `${hostname}/integrations/mcp/oauth-manual`,
        { callbackUrl: manualCallbackUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setTestResult({
          success: true,
          message:
            "Authorization code submitted. The connection test should complete automatically.",
          tools: [],
        });

        // Clear OAuth state and manual input
        setOauthState(null);
        setManualCallbackUrl("");
      }
    } catch (error: any) {
      console.error("Manual OAuth callback error:", error);
      setTestResult({
        success: false,
        message:
          error.response?.data?.error || "Failed to process manual callback",
      });
    } finally {
      setProcessingManualCallback(false);
    }
  };

  const testConnection = async () => {
    if (!serverName || !serverUrl) {
      setTestResult({
        success: false,
        message: "Please fill in server name and URL before testing",
      });
      return;
    }

    setTesting(true);
    setTestResult(null);
    setOauthState(null);
    setManualCallbackUrl("");

    try {
      const token = await getToken();
      const testData: any = {
        serverName,
        serverUrl,
        serverType,
        useOAuth,
        apiKey: apiKey || undefined,
        headers:
          headers.length > 0
            ? headers.reduce((acc, { key, value }) => {
                if (key && value) {
                  acc[key] = value;
                }
                return acc;
              }, {} as Record<string, string>)
            : undefined,
      };

      // Include integration ID and company ID when testing an existing integration
      if (editMode && existingData?.id) {
        testData.integrationId = existingData.id;
        testData.companyId = companyId || existingData.companyId;
      }

      // Use SSE for the connection test
      const sseUrl = `${hostname}/integrations/mcp/test-connection-sse`;

      // Create a POST request with fetch to get the SSE stream
      const fetchResponse = await fetch(sseUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify(testData),
      });

      if (!fetchResponse.ok) {
        throw new Error(`HTTP error! status: ${fetchResponse.status}`);
      }

      // Read the SSE stream manually
      const reader = fetchResponse.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("Unable to read response stream");
      }

      const readStream = async () => {
        let buffer = ""; // Buffer for incomplete lines
        
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            // Decode the chunk and add to buffer
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            // Process complete messages (ending with double newline)
            const messages = buffer.split("\n\n");
            
            // Keep the last incomplete message in buffer
            buffer = messages[messages.length - 1];
            
            // Process all complete messages
            for (let i = 0; i < messages.length - 1; i++) {
              const message = messages[i];
              const lines = message.split("\n");
              
              // Extract data lines and combine them
              let dataStr = "";
              for (const line of lines) {
                if (line.startsWith("data: ")) {
                  // Handle multi-line data by concatenating
                  dataStr += line.slice(6); // Remove 'data: ' prefix
                }
              }
              
              if (dataStr.trim()) {
                try {
                  const data = JSON.parse(dataStr);
                  handleSSEEvent(data);
                } catch (parseErr) {
                  console.error("Error parsing SSE data:", parseErr);
                  console.error("Failed to parse:", dataStr);
                  // Stop spinner and show error when parsing fails
                  setTestResult({
                    success: false,
                    message: "Error processing server response. Please try again.",
                  });
                  setTesting(false);
                  setOauthState(null);
                }
              }
            }
          }
          
          // Process any remaining buffer content
          if (buffer.trim()) {
            const lines = buffer.split("\n");
            let dataStr = "";
            for (const line of lines) {
              if (line.startsWith("data: ")) {
                dataStr += line.slice(6);
              }
            }
            if (dataStr.trim()) {
              try {
                const data = JSON.parse(dataStr);
                handleSSEEvent(data);
              } catch (parseErr) {
                console.error("Error parsing final SSE data:", parseErr);
                // Stop spinner and show error when parsing fails
                setTestResult({
                  success: false,
                  message: "Error processing server response. Please try again.",
                });
                setTesting(false);
                setOauthState(null);
              }
            }
          }
        } catch (streamErr) {
          console.error("Error reading SSE stream:", streamErr);
          setTestResult({
            success: false,
            message: "Connection stream error. Please try again.",
          });
          setTesting(false);
          setOauthState(null);
        }
      };

      // Function to handle SSE events
      const handleSSEEvent = (data: any) => {
        console.log("SSE event received:", data);

        switch (data.type) {
          case "testing":
            // Keep spinner running, just update message
            setTestResult({
              success: true,
              message: data.message,
            });
            break;

          case "auth_required":
            setOauthState(data.state);
            // Open auth URL in new tab
            const safeUrlPattern = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;
            if (safeUrlPattern.test(data.auth_url)) window.open(data.auth_url, "_blank");
            // Keep spinner running while waiting for auth
            setTestResult({
              success: true,
              message:
                "Authorization required. A new tab has been opened for you to authorize. Please paste the callback URL below after authorizing.",
            });
            break;

          case "auth_received":
            // Keep spinner running, auth received but connection still processing
            setTestResult({
              success: true,
              message: data.message,
            });
            break;

          case "waiting":
            // Keep spinner running while waiting
            setTestResult({
              success: true,
              message: data.message,
            });
            break;

          case "success":
            // Final success - stop spinner, show tools
            // Store OAuth tokens if provided
            console.log("OAuth data received:", data);
            if (data.oauth_tokens) {
              setOauthTokens(data.oauth_tokens);
              console.log("OAuth tokens received and stored");
            }
            if (data.oauth_client_info) {
              setOauthClientInfo(data.oauth_client_info);
              console.log("OAuth client info received and stored");
            }

            if (data.tools && data.tools.length > 0) {
              setTestResult({
                success: true,
                message: data.message,
                tools: data.tools,
              });
            } else {
              // Success with tools means final result
              setTestResult({
                success: true,
                message: data.message || "Connection successful",
                tools: data.tools || [],
              });
            }
            setTesting(false); // Stop spinner only on final success
            setOauthState(null);
            break;

          case "error":
            // Final error - stop spinner
            setTestResult({
              success: false,
              message: data.message,
            });
            setTesting(false); // Stop spinner only on final error
            setOauthState(null);
            break;

          default:
            console.log("Unknown SSE event type:", data.type);
        }
      };

      // Start reading the stream
      readStream();
    } catch (error: any) {
      console.error("Error starting connection test:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to start connection test";
      setTestResult({
        success: false,
        message: errorMessage,
      });
      setTesting(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <TextField
        fullWidth
        label="Server URL"
        value={serverUrl}
        onChange={(e) => setServerUrl(e.target.value)}
        required
        margin="normal"
        placeholder="https://example.com"
        helperText="The base URL of your MCP server"
      />

      <TextField
        fullWidth
        label="Description (Optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        margin="normal"
        placeholder="What does this MCP server do?"
        helperText="A brief description of this MCP server's purpose"
        multiline
        rows={3}
      />

      <TextField
        fullWidth
        label="API Key (Optional)"
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
        margin="normal"
        type="password"
        helperText="API key for authentication (if required)"
        disabled={useOAuth}
      />

      <FormControlLabel
        control={
          <Switch
            checked={useOAuth}
            onChange={(e) => {
              setUseOAuth(e.target.checked);
              // Clear API key when switching to OAuth
              if (e.target.checked) {
                setApiKey("");
              }
            }}
          />
        }
        label="Use OAuth Authentication"
        sx={{ mt: 2, mb: 1 }}
      />

      <Box sx={{ mt: 3, mb: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Custom Headers (Optional)
        </Typography>
        {headers.map((header, index) => (
          <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
            <TextField
              size="small"
              placeholder="Header Name"
              value={header.key}
              onChange={(e) => updateHeader(index, "key", e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              placeholder="Header Value"
              value={header.value}
              onChange={(e) => updateHeader(index, "value", e.target.value)}
              sx={{ flex: 1 }}
            />
            <IconButton
              size="small"
              onClick={() => removeHeader(index)}
              color="error"
            >
              <Delete />
            </IconButton>
          </Box>
        ))}
        <Button
          startIcon={<Add />}
          onClick={addHeader}
          size="small"
          sx={{ mt: 1 }}
        >
          Add Header
        </Button>
      </Box>

      {/* Test Connection Section */}
      <Box sx={{ mt: 3, mb: 2 }}>
        <Button
          variant="outlined"
          onClick={testConnection}
          disabled={testing || !serverUrl}
          startIcon={testing ? <CircularProgress size={16} /> : <CheckCircle />}
          sx={{ mb: 2 }}
        >
          {testing ? "Testing Connection..." : "Test Connection"}
        </Button>

        {/* OAuth Authorization Status - shown when OAuth is required */}
        {oauthState && (
          <Paper
            sx={{
              p: 2,
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              OAuth Authorization Required
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              After authorizing in the new tab, the connection will complete
              automatically. You can also paste the callback URL below if
              needed:
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Callback URL (Optional)"
              placeholder="http://localhost:9999/?code=...&state=..."
              value={manualCallbackUrl}
              onChange={(e) => setManualCallbackUrl(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              onClick={handleManualCallback}
              disabled={processingManualCallback || !manualCallbackUrl.trim()}
              startIcon={
                processingManualCallback ? (
                  <CircularProgress size={16} />
                ) : undefined
              }
              size="small"
            >
              {processingManualCallback
                ? "Processing..."
                : "Complete Authorization Manually"}
            </Button>
          </Paper>
        )}

        {testResult && (
          <Alert
            severity={testResult.success ? "success" : "error"}
            icon={testResult.success ? <CheckCircle /> : <ErrorIcon />}
            sx={{ mb: 2 }}
          >
            {testResult.message}

            {testResult.success &&
              testResult.tools &&
              testResult.tools.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Available Tools:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {testResult.tools.map((tool, index) => (
                      <Chip
                        key={index}
                        label={tool.name}
                        size="small"
                        variant="outlined"
                        title={tool.description}
                      />
                    ))}
                  </Box>
                </Box>
              )}
          </Alert>
        )}
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mt: 3, mb: 2 }}>
        <Typography variant="subtitle2" color="primary" gutterBottom>
          About MCP Tools
        </Typography>
        <Typography variant="body2" color="text.secondary">
          MCP (Model Context Protocol) servers provide tools and capabilities
          that your AI agents can use. HTTP servers are accessed via API calls
          and can be hosted anywhere.
        </Typography>
      </Paper>
    </Box>
  );
};

export default MCPIntegrationForm;
